/**
 * SiteTabs — the tab strip of Customer > Site services: Services (site-wide service actions,
 * routes/SiteConfigure), VLANs (the site VLAN table, routes/GatewayVlans) and Mesh (routes/Mesh).
 *
 * OP#1858 (user feedback 2026-10-14): the tabs used to link to `gateway-vlans` and `mesh`, so
 * clicking through them moved the left-menu highlight Customer -> Gateway -> Wireless. Every
 * tab now lives UNDER the Site services section (configure, configure/vlans, configure/mesh;
 * lib/navModel.SITE_SERVICES_TABS), so the menu stays on Site services while you switch tabs.
 *
 * The strip renders itself only on a Site services path (lib/navModel.siteServicesTab): the
 * same GatewayVlans / Mesh screens reached from Gateway > Site VLANs or Wireless > Mesh show NO
 * strip, because there the lit menu item is Gateway / Wireless, and a strip whose tabs jump to
 * another menu is exactly the inconsistency being fixed. Pages can therefore render <SiteTabs />
 * unconditionally.
 *
 * Real routes, not local state: each tab is deep-linkable and survives a reload, the same rule
 * the router follows everywhere (decision #9). Brand violet marks the active tab because a tab
 * is an interactive affordance, never an operational state.
 */
import { Link, useLocation } from "react-router-dom";
import { useSession } from "@/stores/session";
import { SITE_SERVICES_TABS, siteServicesTab } from "@/lib/navModel";
import { cn } from "@/lib/cn";

export function SiteTabs() {
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  const { pathname } = useLocation();
  // Which tab this path is; null = not a Site services page -> no strip at all.
  const active = siteServicesTab(pathname);
  if (!orgId || !siteId || !active) return null;
  const base = `/o/${orgId}/s/${siteId}/configure`;
  return (
    <nav aria-label="Site services" className="mb-4 flex gap-1 border-b border-line">
      {SITE_SERVICES_TABS.map((tab) => (
        <Link
          key={tab.id}
          to={tab.sub ? `${base}/${tab.sub}` : base}
          aria-current={tab.id === active ? "page" : undefined}
          className={cn(
            // 44px touch floor on the tablet; the underline sits on the border it replaces.
            "-mb-px inline-flex min-h-touch items-center border-b-2 px-4 text-sm font-medium transition-colors",
            tab.id === active
              ? "border-brand text-ink"
              : "border-transparent text-ink-muted hover:text-ink",
          )}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}
